import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, Clock, Calendar, ListChecks, FolderKanban, FileText, Users, BarChart3, X, ShieldCheck, MessageSquare,
  MonitorPlay, Images, MonitorDown, PartyPopper, SlidersHorizontal,
} from 'lucide-react';
import clsx from 'clsx';
import { useAuth } from '@/hooks/useAuth';

const employeeNav = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/attendance', label: 'Attendance', icon: Clock },
  { to: '/leaves', label: 'Leaves', icon: Calendar },
  { to: '/holidays', label: 'Holidays', icon: PartyPopper },
  { to: '/tasks', label: 'Tasks', icon: ListChecks },
  { to: '/projects', label: 'Projects', icon: FolderKanban },
  { to: '/chats', label: 'Chats', icon: MessageSquare },
  { to: '/work-reports', label: 'Daily Work', icon: FileText },
  { to: '/desktop-app', label: 'Desktop App', icon: MonitorDown },
];

const adminNav = [
  { to: '/admin', label: 'Overview', icon: LayoutDashboard },
  { to: '/admin/employees', label: 'Employees', icon: Users },
  { to: '/admin/attendance', label: 'Attendance', icon: Clock },
  { to: '/admin/tracker', label: 'Tracker', icon: MonitorPlay },
  { to: '/admin/tracker/screenshots', label: 'Screenshots', icon: Images },
  { to: '/admin/tracker/settings', label: 'Tracker Settings', icon: SlidersHorizontal },
  { to: '/admin/leaves', label: 'Leaves', icon: Calendar },
  { to: '/admin/holidays', label: 'Holidays', icon: PartyPopper },
  { to: '/admin/work-reports', label: 'Work Reports', icon: FileText },
  { to: '/projects', label: 'Projects', icon: FolderKanban },
  { to: '/chats', label: 'Chats', icon: MessageSquare },
  { to: '/admin/reports', label: 'Reports', icon: BarChart3 },
  { to: '/desktop-app', label: 'Desktop App', icon: MonitorDown },
];

export default function Sidebar({ open, onClose }) {
  const { isAdmin, user } = useAuth();
  const links = isAdmin ? adminNav : employeeNav;

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 bg-slate-900/40 z-30 lg:hidden"
          onClick={onClose}
        />
      )}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-40 w-72 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800',
          'flex flex-col transition-transform duration-300 lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-2.5 min-w-0">
            <img
              src="/fevicon.svg"
              alt="EDC"
              className="h-10 w-10 object-contain shrink-0"
            />
            <div className="uppercase leading-[1.05]">
              <div className="text-[13px] font-extrabold tracking-wider text-orange-500">EMPLOYEE</div>
              <div className="text-[10px] whitespace-nowrap">
                <span className="font-medium tracking-[0.3em] text-slate-500 dark:text-slate-300">DESK</span>{' '}
                <span className="font-extrabold tracking-[0.3em] text-sky-500">CRM</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="lg:hidden btn-ghost p-1 shrink-0">
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition',
                  isActive
                    ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300'
                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                )
              }
            >
              <l.icon size={18} />
              <span>{l.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 grid place-items-center text-white font-semibold">
              {user?.name?.[0]?.toUpperCase() || 'U'}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-xs text-slate-500 flex items-center gap-1 truncate">
                {isAdmin && <ShieldCheck size={12} />}
                <span className="truncate">{user?.designation || user?.role}</span>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
